/**
 * Role-based tour step definitions for the in-app walkthrough.
 * Each step targets a CSS selector and shows a tooltip with i18n key.
 * Steps that require navigation include a `route` field.
 */

export interface TourStep {
  target: string;        // CSS selector for the element to highlight
  titleKey: string;      // i18n key for tooltip title
  contentKey: string;    // i18n key for tooltip body
  route?: string;        // Navigate to this route before showing the step
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  tourEvent?: string;    // Custom event detail dispatched before showing this step (for mock data injection)
}

// Operator tour: clock-in, daily queue, production flow
export const operatorSteps: TourStep[] = [
  {
    target: '[data-tour="nav-daily-queue"]',
    titleKey: 'tutorial.operator.dailyQueue.title',
    contentKey: 'tutorial.operator.dailyQueue.content',
    route: '/schedule?tab=daily',
  },
  {
    target: '[data-tour="schedule-session-card"]',
    titleKey: 'tutorial.operator.sessionCard.title',
    contentKey: 'tutorial.operator.sessionCard.content',
    route: '/schedule?tab=daily',
  },
  {
    target: '[data-tour="mock-clock-in-modal"]',
    titleKey: 'tutorial.operator.sessionCardScanIn.title',
    contentKey: 'tutorial.operator.sessionCardScanIn.content',
    route: '/schedule?tab=daily',
    placement: 'left',
    tourEvent: 'clock-in-modal',
  },
  {
    target: '[data-tour="nav-weekly-view"]',
    titleKey: 'tutorial.operator.weeklyView.title',
    contentKey: 'tutorial.operator.weeklyView.content',
    route: '/schedule?tab=weekly',
  },
  {
    target: '[data-tour="clock-in-area"]',
    titleKey: 'tutorial.operator.clockIn.title',
    contentKey: 'tutorial.operator.clockIn.content',
    route: '/operator/production',
    placement: 'left',
    tourEvent: 'scan-station',
  },
  {
    target: '[data-tour="scan-badge-area"]',
    titleKey: 'tutorial.operator.scanBadge.title',
    contentKey: 'tutorial.operator.scanBadge.content',
    route: '/operator/production',
    placement: 'left',
    tourEvent: 'scan-badge',
  },
  {
    target: '[data-tour="scan-success-area"]',
    titleKey: 'tutorial.operator.scanSuccess.title',
    contentKey: 'tutorial.operator.scanSuccess.content',
    route: '/operator/production',
    placement: 'left',
    tourEvent: 'scan-success',
  },
  {
    target: '[data-tour="mock-session-info"]',
    titleKey: 'tutorial.operator.sessionInfo.title',
    contentKey: 'tutorial.operator.sessionInfo.content',
    route: '/operator/production',
    placement: 'bottom',
    tourEvent: 'production-scanning',
  },
  {
    target: '[data-tour="mock-box-scanning"]',
    titleKey: 'tutorial.operator.boxScanning.title',
    contentKey: 'tutorial.operator.boxScanning.content',
    route: '/operator/production',
    placement: 'left',
    tourEvent: 'production-scanning',
  },
  {
    target: '[data-tour="mock-print-label"]',
    titleKey: 'tutorial.operator.printLabel.title',
    contentKey: 'tutorial.operator.printLabel.content',
    route: '/operator/production',
    placement: 'top',
    tourEvent: 'production-scanning',
  },
  {
    target: '[data-tour="mock-badge-label-scan"]',
    titleKey: 'tutorial.operator.badgeLabelScan.title',
    contentKey: 'tutorial.operator.badgeLabelScan.content',
    route: '/operator/production',
    placement: 'left',
    tourEvent: 'production-badge-scan',
  },
  {
    target: '[data-tour="mock-finish-session"]',
    titleKey: 'tutorial.operator.finishSession.title',
    contentKey: 'tutorial.operator.finishSession.content',
    route: '/operator/production',
    placement: 'bottom',
    tourEvent: 'production-finish',
  },
  {
    target: '[data-tour="mock-waste-tracking"]',
    titleKey: 'tutorial.operator.wasteTracking.title',
    contentKey: 'tutorial.operator.wasteTracking.content',
    route: '/operator/production',
    placement: 'left',
    tourEvent: 'production-waste',
  },
];

// Supervisor tour: dashboard, session planning, schedule, reports
export const supervisorSteps: TourStep[] = [
  {
    target: '[data-tour="supervisor-dashboard"]',
    titleKey: 'tutorial.supervisor.dashboard.title',
    contentKey: 'tutorial.supervisor.dashboard.content',
    route: '/supervisor',
  },
  {
    target: '[data-tour="station-cards"]',
    titleKey: 'tutorial.supervisor.stationCards.title',
    contentKey: 'tutorial.supervisor.stationCards.content',
    route: '/supervisor',
  },
  {
    target: '[data-tour="nav-production"]',
    titleKey: 'tutorial.supervisor.productionPlanning.title',
    contentKey: 'tutorial.supervisor.productionPlanning.content',
    route: '/production-planning',
  },
  {
    target: '[data-tour="session-list"]',
    titleKey: 'tutorial.supervisor.sessionList.title',
    contentKey: 'tutorial.supervisor.sessionList.content',
    route: '/production-planning',
  },
  {
    target: '[data-tour="nav-schedule"]',
    titleKey: 'tutorial.supervisor.schedule.title',
    contentKey: 'tutorial.supervisor.schedule.content',
    route: '/schedule',
  },
  {
    target: '[data-tour="nav-warehouse-transfer"]',
    titleKey: 'tutorial.supervisor.transfers.title',
    contentKey: 'tutorial.supervisor.transfers.content',
    route: '/transfers',
  },
  {
    target: '[data-tour="nav-inventory"]',
    titleKey: 'tutorial.supervisor.inventory.title',
    contentKey: 'tutorial.supervisor.inventory.content',
    route: '/inventory',
  },
  {
    target: '[data-tour="nav-reports"]',
    titleKey: 'tutorial.supervisor.reports.title',
    contentKey: 'tutorial.supervisor.reports.content',
    route: '/supervisor/reports',
  },
  {
    target: '[data-tour="report-eod"]',
    titleKey: 'tutorial.supervisor.reportEod.title',
    contentKey: 'tutorial.supervisor.reportEod.content',
    route: '/supervisor/reports',
  },
  {
    target: '[data-tour="report-yield"]',
    titleKey: 'tutorial.supervisor.reportYield.title',
    contentKey: 'tutorial.supervisor.reportYield.content',
    route: '/supervisor/reports',
  },
  {
    target: '[data-tour="nav-production-records"]',
    titleKey: 'tutorial.supervisor.productionRecords.title',
    contentKey: 'tutorial.supervisor.productionRecords.content',
    route: '/production-records',
  },
  {
    target: '[data-tour="nav-settings"]',
    titleKey: 'tutorial.supervisor.settings.title',
    contentKey: 'tutorial.supervisor.settings.content',
    route: '/settings',
  },
];

// Manager tour: manager dashboard + shared supervisor screens
export const managerSteps: TourStep[] = [
  {
    target: '[data-tour="manager-dashboard"]',
    titleKey: 'tutorial.manager.dashboard.title',
    contentKey: 'tutorial.manager.dashboard.content',
    route: '/manager',
  },
  {
    target: '[data-tour="approval-cards"]',
    titleKey: 'tutorial.manager.approvalCards.title',
    contentKey: 'tutorial.manager.approvalCards.content',
    route: '/manager',
  },
  {
    target: '[data-tour="nav-production"]',
    titleKey: 'tutorial.manager.productionPlanning.title',
    contentKey: 'tutorial.manager.productionPlanning.content',
    route: '/production-planning',
  },
  {
    target: '[data-tour="nav-schedule"]',
    titleKey: 'tutorial.manager.schedule.title',
    contentKey: 'tutorial.manager.schedule.content',
    route: '/schedule',
  },
  {
    target: '[data-tour="nav-warehouse-transfer"]',
    titleKey: 'tutorial.manager.transfers.title',
    contentKey: 'tutorial.manager.transfers.content',
    route: '/transfers',
  },
  {
    target: '[data-tour="nav-inventory"]',
    titleKey: 'tutorial.manager.inventory.title',
    contentKey: 'tutorial.manager.inventory.content',
    route: '/inventory',
  },
  {
    target: '[data-tour="nav-reports"]',
    titleKey: 'tutorial.manager.reports.title',
    contentKey: 'tutorial.manager.reports.content',
    route: '/supervisor/reports',
  },
  {
    target: '[data-tour="nav-production-records"]',
    titleKey: 'tutorial.manager.productionRecords.title',
    contentKey: 'tutorial.manager.productionRecords.content',
    route: '/production-records',
  },
];

// Admin tour: supervisor tour + admin-only screens
export const adminSteps: TourStep[] = [
  // Start with admin-specific pages
  {
    target: '[data-tour="nav-dashboard"]',
    titleKey: 'tutorial.admin.dashboard.title',
    contentKey: 'tutorial.admin.dashboard.content',
    route: '/dashboard',
  },
  {
    target: '[data-tour="nav-users"]',
    titleKey: 'tutorial.admin.users.title',
    contentKey: 'tutorial.admin.users.content',
    route: '/users',
  },
  {
    target: '[data-tour="nav-inventory"]',
    titleKey: 'tutorial.admin.inventory.title',
    contentKey: 'tutorial.admin.inventory.content',
    route: '/inventory',
  },
  {
    target: '[data-tour="nav-stations"]',
    titleKey: 'tutorial.admin.stations.title',
    contentKey: 'tutorial.admin.stations.content',
    route: '/stations',
  },
  {
    target: '[data-tour="nav-schedule"]',
    titleKey: 'tutorial.admin.schedule.title',
    contentKey: 'tutorial.admin.schedule.content',
    route: '/schedule',
  },
  {
    target: '[data-tour="nav-settings"]',
    titleKey: 'tutorial.admin.settings.title',
    contentKey: 'tutorial.admin.settings.content',
    route: '/settings',
  },
  {
    target: '[data-tour="settings-ics-users"]',
    titleKey: 'tutorial.admin.icsUsers.title',
    contentKey: 'tutorial.admin.icsUsers.content',
    route: '/settings',
  },
];

/**
 * Returns the tour steps for a given role code.
 */
export function getTourSteps(role: string): TourStep[] {
  switch (role) {
    case 'O':
    case 'operator':
      return operatorSteps;
    case 'S':
    case 'supervisor':
      return supervisorSteps;
    case 'M':
    case 'manager':
      return managerSteps;
    case 'A':
    case 'admin':
      return adminSteps;
    default:
      return adminSteps;
  }
}
